import React from 'react';
import { MapPin, Building2, Map, Hash } from 'lucide-react';
import { AddressAutocomplete } from '../../ui/AddressAutocomplete';
import { streetLineFromPlace } from '../../../lib/utils/addressFromPlace';
import { adminDivisionLabel, postalCodeLabel, isItalyName } from '../../../lib/utils/countries';
import { PropSelectRow, PropTextRow } from './rows';
import { ITALIAN_REGIONS, ITALIAN_PROVINCES, getProvincesByRegion } from '../../../lib/utils/italianRegions';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';

interface AddressSectionRowsProps {
  formData: Record<string, any>;
  updateFormData: (updates: Record<string, any>) => void;
  validationErrors: Record<string, string>;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/**
 * Sezione "Sede legale" a righe di proprietà: autocomplete Google in testa
 * (ristretto al paese scelto nelle righe sopra), poi Città / suddivisione /
 * codice postale adattivi. Gli indirizzi di spedizione hanno una sezione
 * propria nel drawer (OperationalLocationsRows).
 */
export function AddressSectionRows({ formData, updateFormData, validationErrors }: AddressSectionRowsProps) {
  const handleAddressSelect = (details: any) => {
    const city = details?.city || details?.locality || '';
    const province = details?.province || details?.administrative_area_level_2 || details?.state || '';
    const postal_code = details?.postal_code || details?.zip_code || '';
    const region = details?.region || details?.administrative_area_level_1 || '';
    // Via + civico, mai l'indirizzo completo di Google (CAP/città doppi nel contratto)
    const formatted = streetLineFromPlace(details) || formData.address || '';
    updateFormData({ address: formatted, city, province, postal_code, region });
  };

  return (
    <div>
      <PropRow icon={rowIcon(MapPin)} label="Indirizzo">
        <div className="min-w-0 flex-1">
          <AddressAutocomplete
            label=""
            bare
            value={formData.address || ''}
            onChange={(value) => updateFormData({ address: value })}
            onPlaceSelected={handleAddressSelect}
            country={formData.country || ''}
            placeholder="Inizia a digitare l'indirizzo..."
          />
        </div>
      </PropRow>
      {validationErrors.address && <p className="ml-[8.5rem] text-[11px] text-red-500">{validationErrors.address}</p>}

      <div>
        <PropTextRow
          icon={rowIcon(Building2)}
          label="Città"
          value={formData.city || ''}
          onChange={(value) => updateFormData({ city: value })}
          error={validationErrors.city}
        />
        {isItalyName(formData.country) ? (
          <PropSelectRow
            icon={rowIcon(MapPin)}
            label="Provincia"
            value={formData.province || ''}
            onChange={(value) => updateFormData({ province: value })}
            options={(formData.region ? getProvincesByRegion(formData.region) : ITALIAN_PROVINCES).map((p) => ({
              value: p.code,
              label: `${p.name} (${p.code})`,
            }))}
            placeholder="Seleziona provincia..."
            error={validationErrors.province}
          />
        ) : (
          <PropTextRow
            icon={rowIcon(MapPin)}
            label={adminDivisionLabel(formData.country)}
            value={formData.province || ''}
            onChange={(value) => updateFormData({ province: value })}
            error={validationErrors.province}
          />
        )}
        <PropTextRow
          icon={rowIcon(Hash)}
          label={postalCodeLabel(formData.country)}
          value={formData.postal_code || ''}
          onChange={(value) => updateFormData({ postal_code: value })}
          error={validationErrors.postal_code}
        />
        {isItalyName(formData.country) ? (
          <PropSelectRow
            icon={rowIcon(Map)}
            label="Regione"
            value={formData.region || ''}
            onChange={(value) => {
              const updates: Record<string, any> = { region: value };
              // Provincia incoerente con la nuova regione: azzerala
              if (formData.province && value && !getProvincesByRegion(value).some((p) => p.code === formData.province)) {
                updates.province = '';
              }
              updateFormData(updates);
            }}
            options={ITALIAN_REGIONS.map((r) => ({ value: r.code, label: r.name }))}
            placeholder="Seleziona regione..."
          />
        ) : formData.region ? (
          <PropTextRow
            icon={rowIcon(Map)}
            label="Regione"
            value={formData.region || ''}
            onChange={(value) => updateFormData({ region: value })}
          />
        ) : null}
      </div>

    </div>
  );
}
